import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { fmtCurrency, fmtNumber, fmtCurrencyCompact } from '../lib/format.ts';
import type { PivotRequest } from '@statistik/shared-types';
import { Icon } from '../components/Icon.tsx';
import { DateRangePicker } from '../components/DateRangePicker.tsx';
import { TableSkeleton } from '../components/Skeleton.tsx';
import { useDimLabelMaps } from '../lib/useWgIdMap.ts';

const AVAILABLE_DIMS = [
  { value: 'store_name', label: 'Filiale' },
  { value: 'warengruppe_name', label: 'Warengruppe' },
  { value: 'artikel_bezeichnung', label: 'Artikel' },
  { value: 'artikel_nr', label: 'Artikel-Nr' },
  { value: 'kassierer_name', label: 'Kassierer' },
  { value: 'jahr', label: 'Jahr' },
  { value: 'monat', label: 'Monat' },
  { value: 'wochentag', label: 'Wochentag' },
  { value: 'plz', label: 'PLZ' },
];

const MEASURES: { value: PivotRequest['measure']; label: string; icon: 'euro' | 'receipt' | 'reports' }[] = [
  { value: 'umsatz', label: 'Umsatz €', icon: 'euro' },
  { value: 'menge', label: 'Menge (Stk)', icon: 'reports' },
  { value: 'anz_bons', label: 'Anzahl Bons', icon: 'receipt' },
  { value: 'avg_bon', label: 'Ø Bonsumme', icon: 'euro' },
];

const MONAT_NAMES = [
  '',
  'Januar',
  'Februar',
  'März',
  'April',
  'Mai',
  'Juni',
  'Juli',
  'August',
  'September',
  'Oktober',
  'November',
  'Dezember',
];
const WT_NAMES = ['', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

// Render-Obergrenze für die Pivot-Matrix. rowKeys×colKeys werden 1:1 als
// DOM-<td> gerendert; ohne Deckel sprengt eine hoch-kardinale Dimension
// (z. B. "Artikel als Spalte") den Browser-Tab mit Out-of-Memory.
// Die Volldaten bleiben für den CSV-Export erhalten.
const MAX_PIVOT_ROWS = 300;
const MAX_PIVOT_COLS = 60;

function prettify(
  dim: string[],
  key: string,
  maps?: { warengruppe: Map<string, string>; artikel: Map<string, string> },
): string {
  // key kommt mit ' | ' getrennt aus den Dim-Werten
  const parts = key.split(' | ');
  return parts
    .map((raw, i) => {
      const d = dim[i] ?? '';
      if (d === 'monat') return MONAT_NAMES[Number(raw)] ?? raw;
      if (d === 'wochentag') return WT_NAMES[Number(raw)] ?? raw;
      if (d === 'warengruppe_name' && maps?.warengruppe) {
        const id = maps.warengruppe.get(raw);
        if (id) return `${id} — ${raw}`;
      }
      if (d === 'artikel_bezeichnung' && maps?.artikel) {
        const id = maps.artikel.get(raw);
        if (id) return `${id} — ${raw}`;
      }
      return raw;
    })
    .join(' / ');
}

function startOfMonthIso(): string {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().substring(0, 10);
}
function todayIso(): string {
  return new Date().toISOString().substring(0, 10);
}

export function PivotPage() {
  const [params] = useSearchParams();
  const filters = useFilterStore((s) => s.filters);
  const labelMaps = useDimLabelMaps();
  const setRange = useFilterStore((s) => s.setRangeFilter);
  const [rows, setRows] = useState<string[]>(['warengruppe_name']);
  const [columns, setColumns] = useState<string[]>(['monat']);
  const [measure, setMeasure] = useState<PivotRequest['measure']>('umsatz');

  // Preset-Loading aus URL (?preset=wg-monat, filial-wg, kassierer-monat)
  useEffect(() => {
    const preset = params.get('preset');
    if (preset === 'wg-monat') {
      setRows(['warengruppe_name']);
      setColumns(['monat']);
      setMeasure('umsatz');
    } else if (preset === 'filial-wg') {
      setRows(['store_name']);
      setColumns(['warengruppe_name']);
      setMeasure('umsatz');
    } else if (preset === 'kassierer-monat') {
      setRows(['kassierer_name']);
      setColumns(['monat']);
      setMeasure('umsatz');
    }
  }, [params]);

  // Default-Zeitraum: aktueller Monat, wenn noch kein datum-Filter gesetzt ist
  useEffect(() => {
    if (!filters.datum) {
      setRange('datum', startOfMonthIso(), todayIso());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const datumRange = (filters.datum && !Array.isArray(filters.datum)) ? filters.datum : {};

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['pivot', rows, columns, measure, filters],
    queryFn: () => api.pivot({ filters, rows, columns, measure, aggregation: 'sum' }),
    enabled: rows.length > 0,
  });

  // Matrix bauen
  const {
    rowKeys,
    colKeys,
    displayRowKeys,
    displayColKeys,
    rowsTruncated,
    colsTruncated,
    cellMap,
    rowTotals,
    colTotals,
    grandTotal,
    maxAbs,
  } = useMemo(() => {
    const rowKeysSet = new Set<string>();
    const colKeysSet = new Set<string>();
    const cellMap = new Map<string, number>();
    const rowTotals = new Map<string, number>();
    const colTotals = new Map<string, number>();
    let grandTotal = 0;
    let maxAbs = 0;
    for (const c of data?.cells ?? []) {
      const rk = c.rowKey.join(' | ');
      const ck = c.colKey.join(' | ');
      rowKeysSet.add(rk);
      colKeysSet.add(ck);
      cellMap.set(`${rk}::${ck}`, c.wert);
      rowTotals.set(rk, (rowTotals.get(rk) ?? 0) + c.wert);
      colTotals.set(ck, (colTotals.get(ck) ?? 0) + c.wert);
      grandTotal += c.wert;
      if (Math.abs(c.wert) > maxAbs) maxAbs = Math.abs(c.wert);
    }
    // Sortierung: Zeilen nach Summe absteigend; Spalten alphabetisch (Monat-Zahlen sortieren numerisch)
    const rowKeys = [...rowKeysSet].sort(
      (a, b) => (rowTotals.get(b) ?? 0) - (rowTotals.get(a) ?? 0),
    );
    const colKeys = [...colKeysSet].sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (!isNaN(na) && !isNaN(nb)) return na - nb;
      return a.localeCompare(b, 'de');
    });
    // Nur eine begrenzte Matrix rendern (siehe MAX_PIVOT_*). Zeilen sind bereits
    // nach Summe sortiert → einfach abschneiden. Spalten beim Abschneiden nach
    // Spaltensumme priorisieren, damit die größten sichtbar bleiben.
    const rowsTruncated = rowKeys.length > MAX_PIVOT_ROWS;
    const colsTruncated = colKeys.length > MAX_PIVOT_COLS;
    const displayRowKeys = rowsTruncated ? rowKeys.slice(0, MAX_PIVOT_ROWS) : rowKeys;
    const displayColKeys = colsTruncated
      ? [...colKeys]
          .sort(
            (a, b) =>
              Math.abs(colTotals.get(b) ?? 0) - Math.abs(colTotals.get(a) ?? 0),
          )
          .slice(0, MAX_PIVOT_COLS)
      : colKeys;
    return {
      rowKeys,
      colKeys,
      displayRowKeys,
      displayColKeys,
      rowsTruncated,
      colsTruncated,
      cellMap,
      rowTotals,
      colTotals,
      grandTotal,
      maxAbs,
    };
  }, [data]);

  const fmt = (v: number | undefined): string => {
    if (v === undefined) return '–';
    if (measure === 'umsatz' || measure === 'avg_bon') return fmtCurrencyCompact(v);
    return fmtNumber(v);
  };
  const fmtFull = (v: number | undefined): string => {
    if (v === undefined) return '–';
    if (measure === 'umsatz' || measure === 'avg_bon') return fmtCurrency(v);
    return fmtNumber(v);
  };

  function exportCsv() {
    const head = [rows.join(' / '), ...colKeys.map((c) => prettify(columns, c)), 'Summe'].join(';');
    const lines = [head];
    for (const rk of rowKeys) {
      const cells = colKeys.map((ck) => String(cellMap.get(`${rk}::${ck}`) ?? ''));
      lines.push([prettify(rows, rk, labelMaps), ...cells, String(rowTotals.get(rk) ?? '')].join(';'));
    }
    const totalLine = [
      'Summe',
      ...colKeys.map((ck) => String(colTotals.get(ck) ?? '')),
      String(grandTotal),
    ].join(';');
    lines.push(totalLine);
    const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pivot-${rows.join('-')}-x-${columns.join('-')}-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const hasResult = rowKeys.length > 0 && colKeys.length > 0;

  return (
    <div className="space-y-5 w-full">
      {/* Intro */}
      <div className="card">
        <div className="card-body flex items-start gap-4">
          <div className="w-10 h-10 rounded-lg bg-brand-50 dark:bg-brand-950 text-brand-600 dark:text-brand-400 flex items-center justify-center shrink-0">
            <Icon name="table" size={20} />
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">
              Pivot-Auswertung
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              Wähle <strong>Zeilen</strong> (z. B. Warengruppe) und <strong>Spalten</strong>{' '}
              (z. B. Monat) — das Dashboard kreuzt sie zur Matrix und berechnet die gewählte
              Kennzahl pro Zelle. Ideal für Reporting und Excel-Export.
            </p>
          </div>
          <button onClick={exportCsv} disabled={!hasResult} className="btn-secondary">
            <Icon name="download" size={14} />
            <span>CSV exportieren</span>
          </button>
        </div>
      </div>

      {/* Steuerung in 4 Schritten */}
      <div className="card">
        <div className="card-body grid gap-5 md:grid-cols-12">
          {/* Step 1: Zeitraum */}
          <ConfigBlock step={1} title="Zeitraum" className="md:col-span-3">
            <DateRangePicker
              value={datumRange}
              onChange={(r) => setRange('datum', r.min, r.max)}
            />
          </ConfigBlock>

          {/* Step 2: Measure */}
          <ConfigBlock step={2} title="Kennzahl" className="md:col-span-3">
            <div className="grid grid-cols-2 gap-1.5">
              {MEASURES.map((m) => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMeasure(m.value)}
                  className={
                    measure === m.value
                      ? 'pill-btn pill-btn-active'
                      : 'pill-btn pill-btn-idle'
                  }
                >
                  <Icon name={m.icon} size={12} />
                  {m.label}
                </button>
              ))}
            </div>
          </ConfigBlock>

          {/* Step 3: Zeilen */}
          <ConfigBlock step={3} title="Zeilen" hint="bis 3" className="md:col-span-3">
            <DimensionPicker value={rows} onChange={setRows} max={3} />
          </ConfigBlock>

          {/* Step 4: Spalten */}
          <ConfigBlock step={4} title="Spalten" hint="bis 2" className="md:col-span-3">
            <DimensionPicker value={columns} onChange={setColumns} max={2} />
          </ConfigBlock>
        </div>
      </div>

      {/* Resultat */}
      <div className="card">
        <div className="card-header">
          <div>
            <h3 className="card-title">Ergebnis</h3>
            <p className="card-subtitle">
              {hasResult
                ? `${rowKeys.length} Zeilen × ${colKeys.length} Spalten — Gesamtsumme ${fmtFull(grandTotal)}`
                : 'noch keine Daten'}
            </p>
          </div>
          {isFetching && !isLoading && (
            <Icon name="refresh" size={14} className="text-brand-500 animate-spin" />
          )}
        </div>
        {hasResult && (rowsTruncated || colsTruncated) && (
          <div className="px-4 py-2 bg-warning-500/10 border-y border-warning-500/30 text-2xs text-warning-700 dark:text-warning-400 flex items-start gap-2">
            <Icon name="alertTriangle" size={14} className="shrink-0 mt-0.5" />
            <span>
              Sehr große Matrix — angezeigt werden{' '}
              {rowsTruncated
                ? `die Top ${MAX_PIVOT_ROWS} von ${rowKeys.length} Zeilen`
                : `${rowKeys.length} Zeilen`}{' '}
              ×{' '}
              {colsTruncated
                ? `die Top ${MAX_PIVOT_COLS} von ${colKeys.length} Spalten`
                : `${colKeys.length} Spalten`}
              . Für die vollständige Auswertung den CSV-Export nutzen — hoch-kardinale
              Dimensionen wie <strong>Artikel</strong> eignen sich besser als Zeile
              statt als Spalte.
            </span>
          </div>
        )}
        <div className="card-body overflow-auto">
          {isLoading ? (
            <TableSkeleton rows={8} />
          ) : !hasResult ? (
            <EmptyState />
          ) : (
            <table className="text-sm border-collapse w-full">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th className="pivot-th-corner">
                    {rows.map((r) => labelOf(r)).join(' / ')}
                  </th>
                  {displayColKeys.map((ck) => (
                    <th key={ck} className="pivot-th">
                      {prettify(columns, ck, labelMaps)}
                    </th>
                  ))}
                  <th className="pivot-th-total">Σ Zeile</th>
                </tr>
              </thead>
              <tbody>
                {displayRowKeys.map((rk) => (
                  <tr key={rk} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors">
                    <td className="pivot-td-row">{prettify(rows, rk, labelMaps)}</td>
                    {displayColKeys.map((ck) => {
                      const val = cellMap.get(`${rk}::${ck}`);
                      const intensity =
                        val !== undefined && maxAbs > 0
                          ? Math.min(1, Math.abs(val) / maxAbs)
                          : 0;
                      const bg =
                        val !== undefined && val >= 0
                          ? `rgba(31,151,245,${0.05 + 0.35 * intensity})`
                          : val !== undefined
                            ? `rgba(249,112,102,${0.05 + 0.35 * intensity})`
                            : undefined;
                      return (
                        <td
                          key={ck}
                          className="pivot-td"
                          style={{ background: bg }}
                          title={fmtFull(val)}
                        >
                          {fmt(val)}
                        </td>
                      );
                    })}
                    <td className="pivot-td-total">{fmt(rowTotals.get(rk))}</td>
                  </tr>
                ))}
                <tr className="font-semibold bg-slate-50 dark:bg-slate-800/60">
                  <td className="pivot-td-row">Σ Spalte</td>
                  {displayColKeys.map((ck) => (
                    <td key={ck} className="pivot-td-total">
                      {fmt(colTotals.get(ck))}
                    </td>
                  ))}
                  <td className="pivot-td-grand">{fmt(grandTotal)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ────────── helpers ────────── */

function ConfigBlock({
  step,
  title,
  hint,
  className = '',
  children,
}: {
  step: number;
  title: string;
  hint?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={className}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-100 dark:bg-brand-900 text-brand-700 dark:text-brand-300 text-2xs font-bold">
          {step}
        </span>
        <span className="text-2xs uppercase tracking-wider font-semibold text-slate-600 dark:text-slate-300">
          {title}
        </span>
        {hint && (
          <span className="text-2xs text-slate-400 dark:text-slate-500">· {hint}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function DimensionPicker({
  value,
  onChange,
  max,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  max: number;
}) {
  function toggle(dim: string) {
    if (value.includes(dim)) {
      onChange(value.filter((d) => d !== dim));
    } else if (value.length < max) {
      onChange([...value, dim]);
    }
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {AVAILABLE_DIMS.map((d) => {
        const active = value.includes(d.value);
        return (
          <button
            key={d.value}
            onClick={() => toggle(d.value)}
            disabled={!active && value.length >= max}
            className={active ? 'pill-btn pill-btn-active' : 'pill-btn pill-btn-idle'}
            title={
              !active && value.length >= max
                ? `Maximal ${max} möglich`
                : active
                  ? 'Entfernen'
                  : 'Hinzufügen'
            }
          >
            {active && <Icon name="check" size={11} />}
            {d.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="py-12 text-center">
      <div className="w-14 h-14 rounded-full bg-slate-100 dark:bg-slate-800/60 text-slate-400 flex items-center justify-center mx-auto mb-3">
        <Icon name="table" size={22} />
      </div>
      <div className="text-sm font-medium text-slate-600 dark:text-slate-300">
        Keine Daten im aktuellen Filter
      </div>
      <div className="text-xs text-slate-400 dark:text-slate-500 mt-1">
        Wähle einen anderen Zeitraum oder eine andere Dimensions-Kombination.
      </div>
    </div>
  );
}

function labelOf(dim: string): string {
  return AVAILABLE_DIMS.find((d) => d.value === dim)?.label ?? dim;
}
